import React from 'react';
import { Box, Grid2 as Grid } from '@mui/material';
import { forOwn, get, has } from 'lodash';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { CloudinaryConfig, cloudinaryImageUrl } from '../../Services/Cloudinary';
import ButtonWithLoader from '../ButtonWithLoader';
import Avatar from '../DesignSystem/Avatar';
import { TextFieldDefault } from '../Posts/Forms';
import useSpace from '../../Hooks/useSpace';
import { loginPath } from '../../Utils/routes';
import { validateCanComment } from '../../Utils/Post/securedStrategies';
import { isIdea } from '../../Utils/Post';
import useApiMutation from '../../Hooks/useApiMutation';
import queriesConfig from '../../Services/queriesConfig';
import { useModal } from 'mui-modal-provider';
import EntitySubscriptionModal from '../EntitySubscriptionModal';
import useCurrentUser from '../../Hooks/useCurrentUser';

export default function InlineCommentForm({
  post,
  commentId,
  onSubmit,
  autoFocus = false,
  parentPost,
  discussionType = 'public', // Type can be 'public' or 'private' or 'email'
}: any) {
  const postId = post.id;
  const { space } = useSpace();
  const navigate = useNavigate();
  const { currentUser, isLoggedIn } = useCurrentUser();
  const { showModal } = useModal();
  const intl = useIntl();
  const commentPlaceHolder = intl.formatMessage({
    id:
      discussionType === 'private'
        ? 'DISCUSSION.SHOW.COMMENT_PLACEHOLDER.PRIVATE'
        : 'DISCUSSION.SHOW.COMMENT_PLACEHOLDER.PUBLIC',
  });

  const {
    control,
    handleSubmit,
    setError,
    reset,
    formState: { errors },
  } = useForm({
    mode: 'onSubmit',
    criteriaMode: 'all',
    defaultValues: {
      content: '',
    },
  });

  const afterSubmit = () => {
    reset();
    onSubmit();
  };

  let endpoint;
  if (discussionType === 'private' || discussionType === 'email') {
    endpoint = queriesConfig.postDiscussionNote(postId, {
      parentId: commentId,
      type: discussionType,
    });
  } else if (commentId) {
    endpoint = queriesConfig.postCommentForComment(postId, commentId);
  } else {
    endpoint = queriesConfig.postComment(postId);
  }

  const { mutate: createNote, isLoading } = useApiMutation({
    query: endpoint,
    onSuccess: afterSubmit,
    onError: (apiErrors: any) => {
      forOwn(apiErrors, (message) => {
        setError('content', message);
      });
    },
  });

  const handleClick = (event: Event) => {
    event.preventDefault();

    if (!isLoggedIn) {
      navigate(loginPath());
    } else {
      if (!space.entity_subscription) {
        showModal(EntitySubscriptionModal);
      } else {
        validateCanComment(isIdea(post.type) ? parentPost : post);
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(createNote)} noValidate>
      <Grid container spacing={2}>
        <Grid>
          <Box sx={{ pt: 0.5 }}>
            <Avatar
              src={cloudinaryImageUrl(currentUser?.picture?.s3_key, CloudinaryConfig.AVATAR_48)}
              size={48}
              alt={currentUser?.full_name}
              fullName={currentUser?.full_name}
              initials={currentUser?.initials}
            />
          </Box>
        </Grid>
        <Grid size="grow">
          <Box>
            <Controller
              control={control}
              name="content"
              render={({ field: { ref, ...rest } }: any) => (
                <TextFieldDefault
                  {...rest}
                  inputRef={ref}
                  placeholder={commentPlaceHolder}
                  error={has(errors, 'content')}
                  helperText={get(errors, 'content.message')}
                  aria-label={commentPlaceHolder}
                  required
                  multiline
                  autoFocus={autoFocus}
                  onClick={handleClick}
                />
              )}
            />
          </Box>
          <Box sx={{ mt: 2 }}>
            <ButtonWithLoader
              type="submit"
              isFetching={isLoading}
              size="large"
              variant="contained"
              color="primary"
            >
              <FormattedMessage id="BUTTONS.PUBLISH" />
            </ButtonWithLoader>
          </Box>
        </Grid>
      </Grid>
    </form>
  );
}
